import React, { useState, useMemo, useEffect } from "react";
import { FaTimes, FaImage, FaSearch, FaInfoCircle } from "react-icons/fa";

// Template images context - can be updated to load from a specific directory
// For now, we'll use a placeholder structure that can be updated with actual template images
const templateImagesContext = require.context("../../assets/creationoption-thumbnails", false, /\.(gif|webp|png|jpe?g)$/);
const TEMPLATE_IMAGE_BY_FILENAME = templateImagesContext.keys().reduce((acc, key) => {
  const cleanKey = key.replace("./", "");
  acc[cleanKey] = templateImagesContext(key);
  return acc;
}, {});

// Template groups for AI image templates
const TEMPLATE_GROUPS = [
  {
    title: "Write my own prompt",
    items: [
    { 
    title: "Write my own prompt",
    id: "write_own_prompt",
    desc: "Write your own prompt", 
    image: "template_write_own_prompt.gif",
    tags: ["No template", "Write your own prompt"]
    }
    ]
  },
  {
    title: "Apparel",
    items: [
    { 
    title: "Single Image",
    id: "single_image_apparel",
    desc: "Single image of the apparel", 
    image: "template_single_image_apparel.gif",
    tags: ["hero image", "apparel"]
    },
    { 
    title: "2 Image Collage", 
    id: "2_image_collage_apparel",
    desc: "Collage of 2 images", 
    image: "template_2_image_collage_apparel.gif",
    tags: ["collage", "apparel"]
    },
    { 
    title: "3 Image Collage", 
    id: "3_image_collage_apparel",
    desc: "Collage of 3 images", 
    image: "template_3_image_collage_apparel.gif",
    tags: ["collage", "apparel"]
    },
    { 
    title: "5 Image Collage", 
    id: "5_image_collage_apparel",
    desc: "Collage of 5 images",
    image: "template_5_image_collage_apparel.gif",
    tags: ["collage", "apparel"]
    }
    ]
  },
  {
    title: "Saree",
    items: [
    { 
    title: "Single Image", 
    id: "single_image_saree",
    desc: "Single image of the saree", 
    image: "template_single_image_saree.gif",
    tags: ["hero image", "saree"]
    },
    { 
    title: "2 Image Collage", 
    id: "2_image_collage_saree",
    desc: "Collage of 2 images of the saree", 
    image: "template_2_image_collage_saree.gif",
    tags: ["collage", "saree"]
    },
    { 
    title: "3 Image Collage", 
    id: "3_image_collage_saree",
    desc: "Collage of 3 images of the saree", 
    image: "template_3_image_collage_saree.gif",
    tags: ["collage", "saree"]
    },
    { 
    title: "5 Image Collage", 
    id: "5_image_collage_saree",
    desc: "Collage of 5 images of the saree",
    image: "template_5_image_collage_saree.gif",
    tags: ["collage", "saree"]
    }
    ]
  },
  {
    title: "Food",
    items: [
    { 
    title: "Single Image", 
    id: "single_image_food",
    desc: "Single image of the food", 
    image: "template_single_image_food.gif",
    tags: ["hero image", "food"]
    },
    { 
    title: "2 Image Collage", 
    id: "2_image_collage_food",
    desc: "Collage of 2 images of the food", 
    image: "template_2_image_collage_food.gif",
    tags: ["collage", "food"]
    },
    { 
    title: "3 Image Collage", 
    id: "3_image_collage_food",
    desc: "Collage of 3 images of the food", 
    image: "template_3_image_collage_food.gif",
    tags: ["collage", "food"]
    },
    { 
    title: "5 Image Collage", 
    id: "5_image_collage_food",
    desc: "Collage of 5 images of the food",
    image: "template_5_image_collage_food.gif",
    tags: ["collage", "food"]
    }
    ]
  }
];

// Get first template from all groups (default selection)
const getDefaultTemplate = () => {
  const allTemplates = TEMPLATE_GROUPS.flatMap(group => 
    group.items.map(item => ({ ...item, category: group.title }))
  );
  return allTemplates[0]?.id || null;
};

const TemplateCard = ({ item, isSelected, onClick }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <button
      type="button"
      className={`group relative overflow-hidden rounded-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl ${
        isSelected 
          ? 'ring-2 ring-pink-400 ring-offset-2 ring-offset-[#0a0a0a] shadow-lg shadow-pink-400/25' 
          : 'hover:ring-1 hover:ring-white/30'
      }`}
      onClick={onClick}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div className="aspect-square relative">
        {item.image && TEMPLATE_IMAGE_BY_FILENAME[item.image] ? (
          <img
            src={TEMPLATE_IMAGE_BY_FILENAME[item.image]}
            alt={item.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-white/5 to-white/0" />
        )}
        
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        {/* Template ID - Bold text in center-bottom */}
        <div className="absolute bottom-0.5 sm:bottom-1 left-0.5 sm:left-1 right-0.5 sm:right-1 text-center">
          <div className="text-white font-bold text-[7px] sm:text-[10px] lg:text-[9px] leading-tight drop-shadow-lg">
            {item.title}
          </div>
        </div>
        
        {/* Info icon for tooltip - hidden on mobile */}
        <div className="hidden sm:block absolute top-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <FaInfoCircle className="text-white/80 text-[10px] lg:text-[8px]" />
        </div>
        
        {/* Selection indicator */}
        {isSelected && (
          <div className="absolute top-0.5 sm:top-1 right-0.5 sm:right-1 w-3 h-3 sm:w-4 sm:h-4 lg:w-3.5 lg:h-3.5 bg-pink-400 rounded-full flex items-center justify-center shadow-lg">
            <svg className="w-1.5 h-1.5 sm:w-2 sm:h-2 lg:w-1.5 lg:h-1.5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        )}
        
        {/* Hover effect */}
        <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors duration-200" />
        
        {/* Tooltip on hover - hidden on mobile */}
        {showTooltip && (
          <div className="hidden sm:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-black/95 text-white text-xs rounded-lg whitespace-normal w-48 text-center z-10 pointer-events-none">
            <div className="font-semibold mb-1">{item.title}</div>
            <div className="text-white/80">{item.desc}</div>
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1">
              <div className="border-4 border-transparent border-t-black/95" />
            </div>
          </div>
        )}
      </div>
    </button>
  );
};

const TemplateModal = ({
  isOpen,
  onClose,
  selectedTemplate,
  setSelectedTemplate,
  isPreview = false,
  className = ""
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  // Get all categories
  const categories = ["All", ...TEMPLATE_GROUPS.map(g => g.title)];

  // Initialize with default template if none selected
  useEffect(() => {
    if (isOpen && !isPreview && !selectedTemplate) {
      const defaultTemplate = getDefaultTemplate();
      if (defaultTemplate) {
        setSelectedTemplate(defaultTemplate);
      }
    }
  }, [isOpen, isPreview, selectedTemplate, setSelectedTemplate]);

  // Filter templates based on search and category - flatten all items
  const filteredItems = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    
    // Flatten all items from all groups
    const allItems = TEMPLATE_GROUPS.flatMap(group => 
      group.items.map(item => ({ ...item, category: group.title }))
    );
    
    // Filter based on search and category
    const filtered = allItems.filter(item => {
      const matchesSearch = !query || 
        item.id.toLowerCase().includes(query) || 
        item.desc.toLowerCase().includes(query) ||
        (item.tags && item.tags.some(tag => tag.toLowerCase().includes(query)));
      const matchesCategory = selectedCategory === "All" || item.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });

    // If there's a selected template, move it to the front
    if (selectedTemplate) {
      const selectedItem = filtered.find(item => item.id === selectedTemplate);
      if (selectedItem) {
        const otherItems = filtered.filter(item => item.id !== selectedTemplate);
        return [selectedItem, ...otherItems];
      }
    }

    return filtered;
  }, [searchQuery, selectedCategory, selectedTemplate]);

  // Count total filtered templates
  const totalTemplates = filteredItems.length;
  const totalAllTemplates = TEMPLATE_GROUPS.reduce((sum, group) => sum + group.items.length, 0);

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("All");
  };

  // Handle template selection
  const handleTemplateSelect = (templateId) => {
    if (isPreview) return;
    setSelectedTemplate(templateId);
    if (!isPreview) onClose();
  };

  if (!isOpen && !isPreview) return null;

  const Panel = (
    <div className={`glass-effect rounded-3xl border border-white/20 bg-[#0a0a0a]/95 backdrop-blur-3xl p-3 sm:p-6 lg:p-8 w-[95vw] max-w-4xl max-h-[92vh] overflow-hidden flex flex-col ${isPreview ? '' : 'shadow-2xl'}`}>
      {/* Header */}
      <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-4 lg:mb-5 flex-shrink-0">
        <div className="p-1.5 sm:p-2 rounded-lg sm:rounded-xl bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-300/20">
          <FaImage className="text-pink-300 text-sm sm:text-base lg:text-lg" />
        </div>
        <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-white tracking-tight">Choose Your Template</h3>
        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <span className="text-xs sm:text-sm text-gray-400">
            {totalTemplates} {totalTemplates === 1 ? 'template' : 'templates'}
          </span>
          {!isPreview && (
            <button 
              type="button" 
              className="p-1.5 sm:p-2 rounded-lg sm:rounded-xl text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-200" 
              onClick={onClose} 
              title="Close"
            >
              <FaTimes className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Scrollable Content Area - Contains Search/Filters and Template Cards */}
      <div className="flex-1 overflow-y-auto pr-1 sm:pr-2 -mr-1 sm:-mr-2">
        {/* Search and Filters */}
        <div className="mb-2 sm:mb-3 space-y-2 sm:space-y-2.5">
          {/* Search Bar */}
          <div className="relative">
            <FaSearch className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs sm:text-sm" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 sm:pl-11 pr-3 sm:pr-4 py-2 sm:py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-pink-400/50 focus:border-pink-400/50 transition-all"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
              >
                <FaTimes className="w-3 h-3 sm:w-4 sm:h-4" />
              </button>
            )}
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            {categories.map(category => (
              <button
                key={category}
                type="button"
                onClick={() => setSelectedCategory(category)}
                className={`px-2.5 py-1.5 sm:px-3 sm:py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-lg shadow-pink-500/25'
                    : 'bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white border border-white/10'
                }`}
              >
                {category}
              </button>
            ))}
            
            {(searchQuery || selectedCategory !== "All") && (
              <button
                type="button"
                onClick={handleClearFilters}
                className="px-2.5 py-1.5 sm:px-3 sm:py-1.5 rounded-lg text-xs font-medium bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10 transition-all duration-200"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Templates Grid */}
        {totalTemplates === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <FaSearch className="text-gray-600 text-4xl mb-3" />
            <h4 className="text-lg font-semibold text-gray-400 mb-2">No templates found</h4>
            <p className="text-gray-500 mb-3 text-sm">
              Try adjusting your search or filters
            </p>
            <button
              type="button"
              onClick={handleClearFilters}
              className="px-5 py-2 rounded-xl bg-gradient-to-r from-pink-500 to-purple-500 text-white text-sm font-medium hover:shadow-lg hover:shadow-pink-500/25 transition-all duration-200"
            >
              Clear All Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 2xl:grid-cols-12 gap-1.5 sm:gap-2 lg:gap-1.5 pb-2 sm:pb-4">
            {filteredItems.map(item => (
              <TemplateCard
                key={item.id}
                item={item}
                isSelected={selectedTemplate === item.id}
                onClick={() => handleTemplateSelect(item.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (isPreview) {
    return (
      <div className={`absolute z-50 pointer-events-auto ${className}`}>
        {Panel}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10">
        {Panel}
      </div>
    </div>
  );
};

export default TemplateModal;
export { TEMPLATE_GROUPS, getDefaultTemplate };

